import React, { useMemo, useState } from "react";
import { searchCars } from "../services/cars";
import { createConversation } from "../services/chat";
import "../styles/cars.css";

const ClientCars = ({ onNavigate, hasNetworkAccess = false, activeNetworkId = null }) => {
    const [query, setQuery] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");

    const grouped = useMemo(() => {
        const map = new Map();
        for (const it of items || []) {
            const carKey = (it?.car_number || "").trim().toLowerCase();
            const networkKey = Number.isFinite(Number(it?.network_id)) ? Number(it.network_id) : 0;
            const key = `${carKey}::${networkKey}`;
            if (!key) continue;
            if (!map.has(key)) {
                map.set(key, {
                    car_number: it.car_number,
                    network_id: Number.isFinite(Number(it?.network_id)) ? Number(it.network_id) : null,
                    count: 0,
                });
            }
            map.get(key).count += 1;
        }
        return Array.from(map.values()).sort((a, b) => {
            const byCar = String(a.car_number).localeCompare(String(b.car_number));
            if (byCar !== 0) return byCar;
            return Number(a.network_id || 0) - Number(b.network_id || 0);
        });
    }, [items]);

    const runSearch = async () => {
        setError("");
        setInfo("");

        const q = (query || "").trim();
        if (!q) {
            setItems([]);
            setInfo("Введіть номер авто або його частину");
            return;
        }
        if (!hasNetworkAccess) {
            setItems([]);
            setInfo("Пошук авто доступний лише після прив'язки до ОСББ (мережі).");
            return;
        }

        setLoading(true);
        try {
            const res = await searchCars(q);
            setItems(Array.isArray(res?.items) ? res.items : []);
            if ((res?.items || []).length === 0) {
                setInfo("Нічого не знайдено");
            }
        } catch (e) {
            setError(e?.message || "Не вдалося виконати пошук");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateChat = async ({ carNumber, networkId }) => {
        setError("");
        setInfo("");

        const num = (carNumber || "").trim();
        if (!num) return;
        if (!networkId) {
            setError("Не вдалося визначити мережу для цієї машини.");
            return;
        }

        setLoading(true);
        try {
            const conv = await createConversation({
                title: `Авто ${num}`,
                work_mode: "network",
                network_id: networkId,
            });
            setInfo("✅ Переписку створено");
            if (typeof onNavigate === "function") {
                const headId = conv?.head_id || conv?.id;
                if (headId) {
                    localStorage.setItem("pendingChatOpenHeadId_client", String(headId));
                    localStorage.setItem("pendingChatOpenTitle_client", String(conv?.title || `Авто ${num}`));
                }
                onNavigate("chat");
            }
        } catch (e) {
            setError(e?.message || "Не вдалося створити переписку");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cars-container operator-page-width-chat">
            <div className="operator-toolbar-card cars-search-card">
                <div className="cars-search">
                    <input
                        value={query}
                        placeholder="Напр.: АА1234ВК"
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") runSearch();
                        }}
                    />
                    <div className="cars-actions">
                        <button className="add-btn" onClick={runSearch} disabled={loading}>Знайти</button>
                        <button
                            className="add-btn add-btn--secondary"
                            onClick={() => {
                                setQuery("");
                                setItems([]);
                                setError("");
                                setInfo("");
                            }}
                            disabled={loading}
                        >
                            Очистити
                        </button>
                    </div>
                </div>
            </div>

            {loading && <div className="cars-note">Завантаження...</div>}
            {error && <div className="cars-error">❌ {error}</div>}
            {info && !error && <div className="cars-note">{info}</div>}

            <div className="cars-list operator-section-stack">
                {grouped.map((g) => (
                    <div key={`${g.car_number}-${g.network_id || "none"}`} className="cars-item operator-accordion-item">
                        <div className="cars-item-main">
                            <div className="cars-item-title">{g.car_number}</div>
                            <div className="cars-item-subtitle">
                                {g.count > 1 ? `Знайдено записів: ${g.count}` : "Створіть переписку по цьому авто"}
                                {g.network_id ? ` · ОСББ #${g.network_id}${activeNetworkId && Number(g.network_id) === Number(activeNetworkId) ? " • поточна мережа" : ""}` : ""}
                            </div>
                        </div>
                        <div className="cars-item-actions">
                            <button className="add-btn" onClick={() => handleCreateChat({ carNumber: g.car_number, networkId: g.network_id })} disabled={loading}>
                                Створити переписку
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ClientCars;
